// ~/assets/media-gallery.js
if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        this._userInteracted = false;

        this.addEventListener(
          'pointerdown',
          () => {
            this._userInteracted = true;
          },
          { capture: true }
        );

        this.addEventListener(
          'keydown',
          () => {
            this._userInteracted = true;
          },
          { capture: true }
        );

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const btn = mediaToSwitch.querySelector('button');
            if (!btn) return;

            btn.addEventListener('click', () => {
              this._userInteracted = true;
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
            });
          });
        }

        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      onSlideChanged(event) {
        const currentEl = event?.detail?.currentElement;
        if (!currentEl) return;

        const thumbnail = this.elements.thumbnails
          ? this.elements.thumbnails.querySelector(`[data-target="${currentEl?.dataset?.mediaId}"]`)
          : null;

        this.setActiveThumbnail(thumbnail);

        if (this._userInteracted) {
          this.upgradeActiveImages(currentEl);

          // Desktop only: auto-load and play inline videos after user switched slides.
          if (this.mql.matches) {
            this.playActiveMedia(currentEl);
          } else {
            this.pauseAllMediaSafely();
          }
        }
      }

      upgradeActiveImages(rootEl) {
        try {
          const imgs = rootEl.querySelectorAll('img[data-scrubme-sizes-full]');
          imgs.forEach((img) => {
            const full = img.getAttribute('data-scrubme-sizes-full');
            if (full && img.getAttribute('sizes') !== full) {
              img.setAttribute('sizes', full);
            }
          });
        } catch (e) {}
      }

      setActiveMedia(mediaId, prepend) {
        if (!this.elements.viewer) return;

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        const canMoveLayout = !!this._userInteracted;

        if (prepend && canMoveLayout) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        if (canMoveLayout) {
          this.upgradeActiveImages(activeMedia);
        }

        window.setTimeout(() => {
          if (!canMoveLayout) return;

          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }

          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;

          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });

        if (this.mql.matches && this._userInteracted) {
          this.playActiveMedia(activeMedia);
        } else {
          this.pauseAllMediaSafely();
        }

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail?.dataset?.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));

        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        const liveRegion = this.elements?.liveRegion;
        if (!image || !liveRegion) return;

        const a11yStrings = (window && window.accessibilityStrings) || {};
        const template = a11yStrings.imageAvailable || 'Image [index] available';

        const announce = () => {
          try {
            liveRegion.setAttribute('aria-hidden', 'false');
            liveRegion.innerHTML = String(template).replace('[index]', position || '');
            setTimeout(() => {
              liveRegion.setAttribute('aria-hidden', 'true');
            }, 2000);
          } catch (e) {}
        };

        if (image.complete && image.naturalWidth > 0) {
          announce();
        } else {
          image.addEventListener('load', announce, { once: true });
        }
      }

      playActiveMedia(activeItem) {
        this.pauseAllMediaSafely();

        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;

        try {
          deferredMedia.loadContent(false);
        } catch (e) {}

        window.setTimeout(() => {
          try {
            const video = deferredMedia.querySelector('video');
            if (video) {
              video.muted = true;
              video.defaultMuted = true;
              video.playsInline = true;
              video.setAttribute('muted', '');
              video.setAttribute('playsinline', '');
              video.setAttribute('webkit-playsinline', '');

              const playPromise = video.play();
              if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(() => {});
              }
            }
          } catch (e) {}
        }, 100);
      }

      pauseAllMediaSafely() {
        try {
          window.pauseAllMedia();
        } catch (e) {}
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer || !this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}